import React, { useEffect, useState } from "react";

import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Fieldset } from "primereact/fieldset";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { CellType } from "utils/enums";
import { T } from "services/lang/LanguageService";
import { isObjectEmpty } from "utils/utils";
import {
  Filter,
  FilterType,
  ValidationType,
  ValidationTypeInfo,
  commonValidationTypes,
  validationTypes,
} from "../utils/interfaces";
import { extractFilters } from "../utils/utils";
import { ProductTemplateFilterProps } from "./ProductTemplateFilterTypes";
import "./ProductTemplateFilter.scss";

const unsupportedValidations: ValidationType[] = [
  "NOT_EMPTY",
  "REGEX",
  "ENDS_WITH",
  "MAX_LENGTH",
  "MIN_LENGTH",
];

const stringValidations: ValidationType[] = ["STARTS_WITH", "CONTAINS"];

const ProductTemplateFilter: React.FC<ProductTemplateFilterProps> = ({
  defaultFilter,
  secondaryFilter,
  attributes,
  onApplyFilter,
}) => {
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filter[]>(() =>
    defaultFilter ? extractFilters(defaultFilter) : [{ reference: "", validation: "", value: "" }]
  );

  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (secondaryFilter !== null) {
      setFilters(
        secondaryFilter ? extractFilters(secondaryFilter) : [{ reference: "", validation: "", value: "" }]
      );
    }
  }, [secondaryFilter]);

  const handleChange = (e: DropdownChangeEvent | React.ChangeEvent<HTMLInputElement>, i: number) => {
    if (error) setError("");
    const { name, value } = e.target;
    setFilters(prev => prev.map((p, j) => (j === i ? { ...p, [name]: value } : p)));
  };

  const getFilterString = (currentFilters: Filter[]) => {
    const grouped = groupFilters(currentFilters);

    return grouped
      .map(group => {
        const inner = group.map(f => `${f.reference}:${f.validation}:"${f.value}"`).join("|");
        return `(${inner})`;
      })
      .reduce((acc, groupStr, idx) => {
        if (idx === 0) return groupStr;
        const operator = grouped[idx][0].type === FilterType.OR ? "|" : ";";
        return `${acc}${operator}${groupStr}`;
      }, "");
  };

  const onApply = () => {
    onApplyFilter(getFilterString(filters));
    setFilterVisible(false);
  };

  const groupFilters = (currentFilters: Filter[]) => {
    const groups: Filter[][] = [];
    let currentGroup: Filter[] = [];

    currentFilters.forEach((f, idx) => {
      if (idx === 0) {
        currentGroup.push(f);
        return;
      }

      if (!f.isAdditional && f.type) {
        groups.push(currentGroup);
        currentGroup = [f];
      } else {
        currentGroup.push(f);
      }
    });

    if (currentGroup.length) {
      groups.push(currentGroup);
    }

    return groups;
  };

  const hasValidFilters = filters.some(f => f.reference && f.validation && f.value?.trim() !== "");

  const onCopyFilters = () => {
    const filterString = getFilterString(filters);

    if (filterString) {
      navigator.clipboard
        .writeText(filterString)
        .then(() => {
          easasAlert({
            summary: T("alert.success"),
            severity: "success",
            detail: T("alert.filters-copied-to-clipboard"),
          });
        })
        .catch(() => {
          easasAlert({
            summary: T("alert.error"),
            severity: "error",
            detail: T("alert.filters-copy-failed"),
          });
        });
    } else {
      easasAlert({
        summary: T("alert.error"),
        severity: "warn",
        detail: T("alert.no-filters-to-copy"),
      });
    }
  };

  const addNewFilter = (type: FilterType) => {
    const emptyFieldFilter = filters.find(f => !f?.reference || !f?.validation || f?.value === "");
    if (!emptyFieldFilter || isObjectEmpty(filters)) {
      setFilters(prev => [...prev, { reference: "", validation: "", value: "", type }]);
      return;
    }
    setError(T("error.all-filter-fields-are-required"));
  };

  const addNewAdditionalFilter = (reference: string, index: number) => {
    const emptyFieldFilter = filters.find(f => !f?.reference || !f?.validation || f?.value === "");
    if (!emptyFieldFilter || isObjectEmpty(filters)) {
      setFilters(prev => {
        const newFilter = { reference, validation: "", value: "", isAdditional: true };
        const updated = [...prev];
        const lengthOfCurrentReference = updated.filter(f => f.reference === reference).length;

        updated.splice(index + lengthOfCurrentReference, 0, newFilter);
        return updated;
      });
      return;
    }
    setError(T("error.all-filter-fields-are-required"));
  };
  const removeFilter = (i: number) => {
    if (error) setError("");
    setFilters(prev => prev.filter((_, j) => j !== i));
  };

  const setValidationOptions = (cellType: CellType | undefined) => {
    if (!cellType) return [];
    let validationOptions: Readonly<ValidationTypeInfo[]> = [];
    switch (cellType) {
      case CellType.LONG:
      case CellType.DOUBLE:
        validationOptions = validationTypes.filter(
          v => ![...unsupportedValidations, ...stringValidations].includes(v.short as ValidationType)
        );
        break;
      default:
        validationOptions = commonValidationTypes.filter(
          v => !unsupportedValidations.includes(v.short as ValidationType)
        );
        break;
    }
    return validationOptions as ValidationTypeInfo[];
  };

  const getLegendTemplate = (mainFilter: Filter) => {
    return (
      <div className="flex align-items-center gap-2">
        <span>
          {attributes?.find(a => a.referenceName === mainFilter?.reference)?.displayName ||
            mainFilter?.reference}
        </span>
        {mainFilter && mainFilter.reference && mainFilter.validation && mainFilter.value && (
          <Button
            icon="pi pi-plus"
            className="p-button-text w-2rem p-0 p-button-success"
            onClick={() => addNewAdditionalFilter(mainFilter.reference!, filters.indexOf(mainFilter))}
            tooltip={`${T("label.add-additional.or-filter-to")} ${mainFilter.reference}`}
            tooltipOptions={{ position: "bottom" }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Button
        icon="pi pi-filter"
        label={T("word.filters")}
        className="component-editor-button icon-blue"
        onClick={() => setFilterVisible(true)}
      />
      <Sidebar
        visible={filterVisible}
        position="left"
        style={{ width: "35rem" }}
        onHide={() => setFilterVisible(false)}
        blockScroll
      >
        <div className="product-template-table-filter flex flex-column justify-content-between h-full">
          <div className="flex flex-column gap-2">
            <h2>{T("word.filters")}</h2>
            {attributes &&
              groupFilters(filters).map((group, gi) => {
                const mainFilter = group.find(g => !g.isAdditional);
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={gi} className="flex flex-column gap-2">
                    {gi !== 0 && (
                      <Divider align="center" className="m-0">
                        {group[0].type === FilterType.OR ? T("word.or") : T("word.and")}
                      </Divider>
                    )}
                    <Fieldset
                      legend={mainFilter?.reference ? getLegendTemplate(mainFilter) : undefined}
                      className="p-2"
                    >
                      {group.map((f, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="flex flex-column gap-2">
                          {f.isAdditional && (
                            <Divider align="center" className="m-0">
                              {T("word.or")}
                            </Divider>
                          )}
                          <div className="flex gap-2 items-center justify-content-end mb-2">
                            {!f.isAdditional && (
                              <Dropdown
                                value={f.reference}
                                options={attributes}
                                onChange={e => handleChange(e, filters.indexOf(f))}
                                name="reference"
                                optionLabel="displayName"
                                optionValue="referenceName"
                                placeholder={T("word.attribute")}
                                className="w-10rem"
                              />
                            )}
                            <Dropdown
                              value={f.validation}
                              options={setValidationOptions(
                                attributes?.find(a => a?.referenceName === f.reference)?.type
                              )}
                              onChange={e => handleChange(e, filters.indexOf(f))}
                              name="validation"
                              optionLabel="display"
                              optionValue="short"
                              placeholder={T("word.validation")}
                              className="w-9rem flex-shrink-0"
                            />
                            <InputText
                              value={f.value}
                              onChange={e => handleChange(e, filters.indexOf(f))}
                              name="value"
                              placeholder={T("word.value")}
                              className="w-8rem flex-shrink-0"
                            />
                            <Button
                              icon="pi pi-times"
                              className="dashboard-editor-button icon-red w-2rem"
                              onClick={() => removeFilter(filters.indexOf(f))}
                            />
                          </div>
                        </div>
                      ))}
                    </Fieldset>
                  </div>
                );
              })}

            {error && <span className="p-error">{error}</span>}
          </div>
          <div className="flex justify-content-between">
            <div className="flex flex-column gap-2">
              <Button
                label={`${T("action.add-filter")} ${filters.length > 0 ? `(${T("word.and")})` : ""}`}
                icon="pi pi-plus"
                onClick={() => addNewFilter(FilterType.AND)}
                className="w-10rem component-editor-button icon-green"
              />
              {filters.length > 0 && (
                <Button
                  label={`${T("action.add-filter")} (${T("word.or")})`}
                  icon="pi pi-plus"
                  onClick={() => addNewFilter(FilterType.OR)}
                  className="w-10rem component-editor-button icon-green"
                />
              )}
            </div>
            <Button
              label={T("action.copy-current-filters")}
              icon="pi pi-copy"
              className="component-editor-button icon-blue align-self-end"
              onClick={onCopyFilters}
              disabled={!hasValidFilters}
            />
            <Button
              label={T("action.apply")}
              icon="pi pi-check"
              className="w-8rem align-self-end component-editor-button icon-green"
              onClick={onApply}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default ProductTemplateFilter;
